import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Bell, CheckCircle, Trash, BellRinging, BellSlash, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useWebPush } from "@/hooks/useWebPush";
import { useBranding } from "@/contexts/BrandingContext";

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `il y a ${diffH} h`;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const branding = useBranding();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [open, setOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const prevUnreadRef = useRef(0);
  const firstLoadRef = useRef(true);
  const { supported: pushSupported, iosNeedsInstall, subscribed: pushSubscribed, busy: pushBusy, enable: pushEnable, disable: pushDisable, lastError: pushError } = useWebPush();

  // Sync the home-screen icon badge counter with the unread count via
  // the Badging API. iOS Safari 16.4+ implements `setAppBadge` on the
  // navigator object when the page runs in standalone PWA mode — this
  // is what produces the red bubble counter on the home-screen icon.
  // Other browsers (Android Chrome, desktop) implement it too. The
  // method is no-op when unsupported.
  useEffect(() => {
    try {
      if (typeof navigator === "undefined") return;
      if (typeof navigator.setAppBadge === "function") {
        if (unread > 0) navigator.setAppBadge(unread).catch(() => {});
        else if (typeof navigator.clearAppBadge === "function") navigator.clearAppBadge().catch(() => {});
      }
    } catch { /* badging not supported — silent */ }
  }, [unread]);

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      const prev = prevUnreadRef.current;
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
      // Pulse + toast + (if granted) OS-level notification when a NEW one arrives
      if (!firstLoadRef.current && (data.unread_count || 0) > prev) {
        setPulse(true);
        setTimeout(() => setPulse(false), 2000);
        const latest = (data.items || []).find(n => !n.read);
        if (latest) {
          toast(latest.title, { description: latest.body });
          try {
            if (typeof window !== "undefined" && "Notification" in window &&
                Notification.permission === "granted" && document.visibilityState !== "visible") {
              const n = new Notification(latest.title, {
                body: latest.body,
                icon: branding.logoUrl || "/brand/sdrive-logo.png",
                tag: latest.id,
              });
              n.onclick = () => { window.focus(); if (latest.link) navigate(latest.link); n.close(); };
            }
          } catch {}
        }
      }
      prevUnreadRef.current = data.unread_count || 0;
      firstLoadRef.current = false;
    } catch {
      // silent
    }
  }, [navigate]);

  // Ask for permission once on first mount (silent if already granted/denied)
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && "Notification" in window &&
          Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchItems();
    const iv = setInterval(fetchItems, 15000);
    return () => clearInterval(iv);
  }, [fetchItems]);

  const handleClick = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n.id}/read`); } catch {}
    fetchItems();
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); toast.success("Tout marqué comme lu"); fetchItems(); }
    catch { toast.error("Échec"); }
  };

  const deleteOne = async (e, n) => {
    e.stopPropagation();
    try { await api.delete(`/notifications/${n.id}`); fetchItems(); }
    catch { toast.error("Échec"); }
  };

  const togglePush = async () => {
    if (pushSubscribed) {
      await pushDisable();
      toast.success("Notifications push désactivées");
      return;
    }
    const res = await pushEnable();
    if (res.ok) {
      toast.success("Notifications push activées");
    } else if (res.reason === "denied") {
      toast.error("Notifications refusées — autorisez-les dans les réglages du navigateur");
    } else if (res.reason === "ios-needs-install") {
      toast.error("Sur iPhone, ajoutez d'abord l'app à l'écran d'accueil (Partager → Sur l'écran d'accueil)");
    } else if (res.reason === "no-vapid") {
      toast.error("Configuration push manquante côté serveur");
    } else if (res.reason === "no-pushmanager" || res.reason === "no-serviceworker" || res.reason === "no-notification-api") {
      toast.error("Ce navigateur ne supporte pas les notifications push");
    } else {
      toast.error(`Échec activation push : ${res.reason || "erreur inconnue"}`);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/notifications/test");
      const bellOk = data?.bell === "ok";
      const push = data?.push || {};
      const subs = push.subscriptions || 0;
      const delivered = push.delivered || 0;
      if (bellOk && delivered > 0) {
        toast.success(`Test envoyé · cloche + ${delivered}/${subs} push`);
      } else if (bellOk && subs === 0) {
        toast.info("Test envoyé dans la cloche · push non activé sur cet appareil");
      } else if (bellOk) {
        toast.warning(`Cloche OK · ${push.failed || 0}/${subs} push échoués${push.error_code ? ` (code ${push.error_code})` : ""}`);
      } else {
        toast.error("Échec du test de notification");
      }
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du test");
    } finally {
      setTesting(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="notifications-bell"
          variant="ghost"
          size="sm"
          className={`relative text-zinc-400 hover:text-white hover:bg-zinc-800 ${pulse ? "animate-pulse" : ""}`}
          title="Notifications"
        >
          <Bell size={18} weight={unread > 0 ? "fill" : "regular"} className={unread > 0 ? "text-amber-400" : ""} />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white flex items-center justify-center ${pulse ? "ring-2 ring-amber-400 ring-offset-1 ring-offset-[#050505]" : ""}`}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-zinc-700 text-white min-w-[340px] max-w-[400px] p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
          <p className="text-sm font-medium text-white">Notifications</p>
          {unread > 0 && (
            <button data-testid="mark-all-read" onClick={markAllRead} className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1">
              <CheckCircle size={12} /> Tout lire
            </button>
          )}
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-8 text-center text-xs text-zinc-500" data-testid="notifications-empty">
              Aucune notification
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-${n.id}`}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-3 py-2.5 border-b border-zinc-800/60 hover:bg-zinc-800/60 transition-colors group ${!n.read ? "bg-amber-500/5" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] leading-tight ${!n.read ? "text-white font-medium" : "text-zinc-300"}`}>{n.title}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{n.body}</p>
                    <p className="text-[10px] text-zinc-600 mt-0.5">{formatTime(n.created_at)}</p>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`del-${n.id}`}
                    onClick={(e) => deleteOne(e, n)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") deleteOne(e, n); }}
                    className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-opacity cursor-pointer select-none"
                    aria-label="Supprimer"
                  >
                    <Trash size={12} />
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
        <DropdownMenuSeparator className="bg-zinc-800 m-0" />
        <div className="px-3 py-2 space-y-2 bg-[#141414]">
          <div className="flex items-center justify-between gap-2">
            <span data-testid="push-status-indicator" className="inline-flex items-center gap-1.5 text-[11px]">
              {pushSubscribed ? (
                <><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /><span className="text-emerald-400">Push actif</span></>
              ) : iosNeedsInstall ? (
                <><span className="w-1.5 h-1.5 rounded-full bg-sky-500" /><span className="text-sky-400" title="Sur iPhone, le push web ne fonctionne qu'après ajout à l'écran d'accueil">iOS : ajoutez à l'écran d'accueil</span></>
              ) : pushSupported ? (
                <><span className="w-1.5 h-1.5 rounded-full bg-zinc-600" /><span className="text-zinc-500">Push inactif</span></>
              ) : (
                <><span className="w-1.5 h-1.5 rounded-full bg-zinc-700" /><span className="text-zinc-600" title={pushError || "Navigateur sans support Push"}>Push non supporté</span></>
              )}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="test-notification-btn"
                onClick={sendTest}
                disabled={testing}
                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 disabled:opacity-50"
                title="Envoyer une notification de test"
              >
                <PaperPlaneTilt size={11} weight="fill" />
                {testing ? "..." : "Tester"}
              </button>
              {(pushSupported || iosNeedsInstall) && (
                <button
                  data-testid="push-toggle-bell"
                  onClick={togglePush}
                  disabled={pushBusy}
                  className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border disabled:opacity-50 ${pushSubscribed ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}
                  title={pushSubscribed ? "Désactiver le push" : "Activer le push"}
                >
                  {pushSubscribed ? <BellRinging size={11} weight="fill" /> : <BellSlash size={11} />}
                  {pushSubscribed ? "Activé" : "Activer"}
                </button>
              )}
            </div>
          </div>
          <p className="text-[10px] text-zinc-600 text-center">Les notifications lues sont supprimées après 30 jours.</p>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
